import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Field, Form as FinalForm } from "react-final-form";
import * as Sentry from "@sentry/browser";
import * as queryString from "query-string";
import {
  composeValidators,
  minLength,
  required,
  validEmail,
  Loading,
} from "@ottatech/shared-components";
import { Button, ErrorText, InputField, Spacing } from "@ottatech/design";
import { pushAnalyticsEvent } from "@ottatech/analytics";

import { CurrentUserDocument } from "schema";

export function LoginForm(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();

  const { data, refetch } = useQuery(CurrentUserDocument);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"INVALID" | boolean>(false);

  const queryParams = queryString.parse(location.search);
  const redirect = queryParams.redirect;

  useEffect(() => {
    if (!data?.currentUser?.id) {
      return;
    }

    if (redirect && typeof redirect === "string") {
      navigate(redirect);
    } else {
      navigate("/");
    }
  }, [data, navigate, redirect]);

  const onSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setLoading(true);
    setError(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
          credentials: "include",
        }
      );

      if (response.status === 403) {
        return setError("INVALID");
      } else if (response.status !== 200) {
        throw new Error("Invalid response received from signin api");
      }

      const { data } = await refetch();

      if (!data.currentUser) {
        throw new Error("No user returned after signing in");
      }
      pushAnalyticsEvent({ eventName: "Candidate logged in" });
    } catch (error) {
      Sentry.captureException(error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FinalForm onSubmit={onSubmit}>
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit} data-testid="login-form">
          <Spacing size={2}>
            <Spacing size={-1}>
              <Field
                name="email"
                validate={composeValidators(required, validEmail, minLength(5))}
              >
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    data-testid="input-email"
                    type="email"
                    label="Email"
                    error={meta.touched && meta.error}
                    autoFocus
                  />
                )}
              </Field>
              <Field
                name="password"
                validate={composeValidators(required, minLength(8))}
              >
                {({ input, meta }) => (
                  <InputField
                    {...input}
                    type="password"
                    label="Password"
                    data-testid="input-password"
                    error={meta.touched && meta.error}
                  />
                )}
              </Field>
            </Spacing>
            {loading && <Loading />}
            {error && (
              <ErrorText data-testid="login-error">
                {error === "INVALID"
                  ? "Incorrect username or password"
                  : "Something went wrong, try again"}
              </ErrorText>
            )}
            <Button
              level="primary"
              type="submit"
              data-testid="login-button"
              style={{ width: "100%" }}
              disabled={loading}
            >
              Log in
            </Button>
          </Spacing>
        </form>
      )}
    </FinalForm>
  );
}
